// Wire codecs for the @radish/primitives value types (plan §4.2 "Money and rates on the wire"). Each is a Zod 4 codec:
// the wire side is a JSON-safe string shape, the decoded side is the bigint-backed primitive. No JSON number ever
// carries money, a rate or a quantity, so precision never depends on a float. OpenAPI publishes the wire side.
import { z } from 'zod';
import {
  asId, money, qty, rate,
  type Currency, type Id, type Money as MoneyValue, type Qty as QtyValue, type Rate as RateValue,
} from '@radish/primitives';

/** A canonical decimal integer: no leading zeros, no plus sign, no '-0'. One value has one spelling. */
const INTEGER = /^(0|-?[1-9]\d*)$/;
const NON_NEGATIVE_INTEGER = /^(0|[1-9]\d*)$/;

const CURRENCIES = ['USD', 'CAD'] as const satisfies readonly Currency[];

const bigintField = (value: unknown, field: string): boolean =>
  typeof value === 'object' && value !== null && typeof (value as Record<string, unknown>)[field] === 'bigint';

/** Money: `{ minor: "1250", currency: "USD" }` on the wire ⇄ `Money` (bigint minor units) in code. */
export const Money = z
  .codec(
    z.object({ minor: z.string().regex(INTEGER), currency: z.enum(CURRENCIES) }),
    z.custom<MoneyValue>((v) => bigintField(v, 'minor')),
    {
      decode: (wire) => money(BigInt(wire.minor), wire.currency),
      encode: (value) => ({ minor: value.minor.toString(), currency: value.currency }),
    },
  )
  .meta({ id: 'Money', description: 'Integer minor units (cents) as a canonical decimal string, plus the currency.' });

/** Rate: `"7250000"` (parts per 10^8, so 7.25 %) on the wire ⇄ `Rate` in code. */
export const Rate = z
  .codec(z.string().regex(NON_NEGATIVE_INTEGER), z.custom<RateValue>((v) => bigintField(v, 'scaled')), {
    decode: (wire) => rate(BigInt(wire)),
    encode: (value) => value.scaled.toString(),
  })
  .meta({ id: 'Rate', description: 'Parts per 10^8 as a canonical decimal string: "100000000" is 100 %, "7250000" is 7.25 %.' });

/** Qty: `"1500"` (thousandths of a unit, so 1.5) on the wire ⇄ `Qty` in code. */
export const Qty = z
  .codec(z.string().regex(NON_NEGATIVE_INTEGER), z.custom<QtyValue>((v) => bigintField(v, 'milli')), {
    decode: (wire) => qty(BigInt(wire)),
    encode: (value) => value.milli.toString(),
  })
  .meta({ id: 'Qty', description: 'Thousandths of a unit as a canonical decimal string: "1000" is one each.' });

/**
 * SignedQty: a Qty that may be below zero, `"-1000"` on the wire ⇄ `Qty` in code, for a figure that nets returns against
 * sales (legacy's `SUM(quantity)`), such as a customer's Favorite Items row whose returns outweigh its sales.
 */
export const SignedQty = z
  .codec(z.string().regex(INTEGER), z.custom<QtyValue>((v) => bigintField(v, 'milli')), {
    decode: (wire) => qty(BigInt(wire)),
    encode: (value) => value.milli.toString(),
  })
  .meta({ id: 'SignedQty', description: 'Thousandths of a unit as a canonical signed decimal string: "-1000" is one returned more than sold.' });

/** A branded UUIDv7 id codec: the wire is the plain string, the decoded value is the primitives `Id<Name>` brand. */
function idCodec<const Name extends string>(name: Name) {
  return z
    .codec(z.uuid({ version: 'v7' }), z.custom<Id<Name>>((v) => typeof v === 'string'), {
      decode: (wire) => asId<Name>(wire),
      encode: (value) => value,
    })
    .meta({ id: name, description: `${name}: a UUIDv7.` });
}

export const AccountId = idCodec('AccountId');
export const LocationId = idCodec('LocationId');
export const DeviceId = idCodec('DeviceId');
export const EmployeeId = idCodec('EmployeeId');
export const ProductId = idCodec('ProductId');
export const OrderId = idCodec('OrderId');
export const OrderLineId = idCodec('OrderLineId');
/** A check (ticket) of a split order. */
export const CheckId = idCodec('CheckId');
export const TenderId = idCodec('TenderId');
export const AdjustmentId = idCodec('AdjustmentId');
export const TillSessionId = idCodec('TillSessionId');
export const CashMovementId = idCodec('CashMovementId');
export const CashCountId = idCodec('CashCountId');
export const CloseoutId = idCodec('CloseoutId');
export const CategoryId = idCodec('CategoryId');
export const DiscountRuleId = idCodec('DiscountRuleId');
export const TaxProfileId = idCodec('TaxProfileId');
export const VariantId = idCodec('VariantId');
export const ModifierGroupId = idCodec('ModifierGroupId');
export const ModifierOptionId = idCodec('ModifierOptionId');
export const DepartmentId = idCodec('DepartmentId');
export const CourseId = idCodec('CourseId');
export const DiningOptionId = idCodec('DiningOptionId');
export const AgeRestrictionId = idCodec('AgeRestrictionId');
export const TaxExemptionId = idCodec('TaxExemptionId');
export const PrinterId = idCodec('PrinterId');
export const FloorPlanId = idCodec('FloorPlanId');
/** A floor-plan table or a free table: the table an order is seated at. */
export const TableId = idCodec('TableId');
export const ZoneId = idCodec('ZoneId');
export const FreeTableId = idCodec('FreeTableId');
export const OnlineStoreId = idCodec('OnlineStoreId');
/** A QR Table Pay code on a table (legacy `qr_codes`). */
export const QrCodeId = idCodec('QrCodeId');
/** A QR Table Pay template (legacy `qr_code_templates`): a location's own, or a premade one shared by every account. */
export const QrTemplateId = idCodec('QrTemplateId');
export const CustomerId = idCodec('CustomerId');
export const CustomerAddressId = idCodec('CustomerAddressId');
export const VoidReasonId = idCodec('VoidReasonId');
export const RefundId = idCodec('RefundId');
export const TenderTypeId = idCodec('TenderTypeId');
export const TimeSegmentId = idCodec('TimeSegmentId');
export const TimecardId = idCodec('TimecardId');
export const BreakId = idCodec('BreakId');
export const BreakPolicyId = idCodec('BreakPolicyId');
export const UserId = idCodec('UserId');
export const RoleId = idCodec('RoleId');
export const InviteId = idCodec('InviteId');
export const JobRoleId = idCodec('JobRoleId');
export const TipSourceId = idCodec('TipSourceId');
export const TipPaidInId = idCodec('TipPaidInId');
export const ScheduleId = idCodec('ScheduleId');
export const ShiftId = idCodec('ShiftId');
export const ScheduleTemplateId = idCodec('ScheduleTemplateId');
export const TimeOffRequestId = idCodec('TimeOffRequestId');
export const GiftCardId = idCodec('GiftCardId');
export const KitchenTicketId = idCodec('KitchenTicketId');
export const PlanId = idCodec('PlanId');
export const PlanVersionId = idCodec('PlanVersionId');
export const BillingAccountId = idCodec('BillingAccountId');
export const BillingAdjustmentId = idCodec('BillingAdjustmentId');
export const LocationSubscriptionId = idCodec('LocationSubscriptionId');
export const BillingInvoiceId = idCodec('BillingInvoiceId');

/** A bigint count of thousandths (core's `qtyMilli`): `"1500"` on the wire ⇄ `1500n` in code. */
export const Milli = z
  .codec(z.string().regex(NON_NEGATIVE_INTEGER), z.bigint().nonnegative(), {
    decode: (wire) => BigInt(wire),
    encode: (value) => value.toString(),
  })
  .meta({ id: 'Milli', description: 'Thousandths of a unit as a canonical decimal string (core qtyMilli): "1000" is one each.' });

/** A signed bigint (a report figure: minor units, seconds, counts): `"-250"` on the wire ⇄ `-250n` in code. */
export const Integer = z
  .codec(z.string().regex(INTEGER), z.bigint(), {
    decode: (wire) => BigInt(wire),
    encode: (value) => value.toString(),
  })
  .meta({ id: 'Integer', description: 'A signed integer as a canonical decimal string; report figures in their unit (minor units, seconds, counts).' });

/** An instant as core holds it (epoch ms, a bigint) ⇄ an ISO-8601 instant on the wire. */
export const EpochMs = z
  .codec(z.iso.datetime({ offset: true }), z.bigint(), {
    decode: (wire) => BigInt(Date.parse(wire)),
    encode: (value) => new Date(Number(value)).toISOString(),
  })
  .meta({ id: 'EpochMs', description: 'An instant, ISO-8601 with an offset; core holds it as epoch milliseconds.' });

/** A calendar date 'YYYY-MM-DD' (a business date). */
export const LocalDate = z.iso.date().meta({ id: 'LocalDate' });

/** An instant with an explicit offset, e.g. '2026-10-10T23:15:00Z'. */
export const IsoInstant = z.iso.datetime({ offset: true }).meta({ id: 'IsoInstant' });

/** A wall-clock time 'HH:mm' on a 15-minute step, as `business_day.start_time` takes. */
export const QuarterHourTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):(00|15|30|45)$/)
  .meta({ id: 'QuarterHourTime', description: 'Wall-clock HH:mm on a 15-minute step.' });

/** A wall-clock time 'HH:mm' on any minute (discount schedule windows; an end before the start crosses midnight). */
export const WallClockTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
  .meta({ id: 'WallClockTime', description: 'Wall-clock HH:mm in the location time zone.' });
